import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  CircularProgress,
  Avatar,
  TablePagination,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import VisibilityIcon from '@material-ui/icons/Visibility';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import RefreshIcon from '@material-ui/icons/Refresh';
import Alert from '@material-ui/lab/Alert';
import { useNavigate } from 'react-router-dom';
import { firestore } from '../../firebase/config';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';

import PageHeader from '../../components/ui/PageHeader';
import EmptyState from '../../components/ui/EmptyState';
import StatusChip from '../../components/ui/StatusChip';
import SearchBar from '../../components/ui/SearchBar';

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  search: { maxWidth: 360, flexGrow: 1 },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 280,
  },
  userCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  userAvatar: {
    width: 36,
    height: 36,
    background: 'linear-gradient(135deg, #6366f1, #ec4899)',
    color: '#fff',
    fontSize: '0.85rem',
  },
  userName: { fontWeight: 600 },
  userId: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  count: {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
  },
}));

const initialsOf = (str) => {
  const s = (str || '').trim();
  if (!s) return '?';
  const parts = s.split(' ');
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
};

const formatDate = (val) => {
  if (!val) return '—';
  const d = val.toDate ? val.toDate() : new Date(val);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const UsersListPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(firestore, 'users'));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setUsers(list);
    } catch (error) {
      console.error('Error fetching users:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la récupération des utilisateurs',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    try {
      await deleteDoc(doc(firestore, 'users', userToDelete.id));
      setUsers((prev) => prev.filter((u) => u.id !== userToDelete.id));
      setSnackbar({
        open: true,
        message: 'Utilisateur supprimé avec succès',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error deleting user:', error);
      setSnackbar({
        open: true,
        message: "Erreur lors de la suppression de l'utilisateur",
        severity: 'error',
      });
    } finally {
      setOpenDeleteDialog(false);
      setUserToDelete(null);
    }
  };

  const filteredUsers = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return users.filter(
      (u) =>
        (u.displayName || u.name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q) ||
        (u.role || '').toLowerCase().includes(q)
    );
  }, [users, searchTerm]);

  const paged = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box className="fade-in-up">
      <PageHeader
        breadcrumbs={[{ label: 'Admin', to: '/admin' }, { label: 'Utilisateurs' }]}
        title="Utilisateurs"
        subtitle={`${filteredUsers.length} utilisateur${filteredUsers.length > 1 ? 's' : ''}`}
        actions={
          <>
            <Tooltip title="Rafraîchir">
              <IconButton
                onClick={fetchUsers}
                style={{
                  background: '#fff',
                  border: '1px solid rgba(17,24,39,0.08)',
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/admin/users/create')}
            >
              Nouvel utilisateur
            </Button>
          </>
        }
      />

      <Paper>
        <Box className={classes.toolbar}>
          <Box className={classes.search}>
            <SearchBar
              value={searchTerm}
              onChange={(v) => {
                setSearchTerm(v);
                setPage(0);
              }}
              placeholder="Rechercher par nom, email, rôle…"
            />
          </Box>
          <Typography className={classes.count}>
            {filteredUsers.length} résultat{filteredUsers.length > 1 ? 's' : ''}
          </Typography>
        </Box>

        {loading ? (
          <Box className={classes.loading}>
            <CircularProgress />
          </Box>
        ) : filteredUsers.length === 0 ? (
          <EmptyState
            icon={<PersonOutlineIcon style={{ fontSize: 36 }} />}
            title="Aucun utilisateur"
            description={
              searchTerm
                ? 'Aucun utilisateur ne correspond à votre recherche.'
                : "Commencez par créer un premier utilisateur."
            }
            actionLabel={!searchTerm ? 'Créer un utilisateur' : undefined}
            onAction={() => navigate('/admin/users/create')}
          />
        ) : (
          <>
            <TableContainer style={{ border: 'none', borderRadius: 0 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Utilisateur</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Rôle</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Créé le</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map((u) => (
                    <TableRow key={u.id} hover>
                      <TableCell>
                        <Box className={classes.userCell}>
                          <Avatar
                            src={u.photoURL || undefined}
                            className={classes.userAvatar}
                          >
                            {initialsOf(u.displayName || u.name || u.email)}
                          </Avatar>
                          <Box>
                            <Box className={classes.userName}>
                              {u.displayName || u.name || '—'}
                            </Box>
                            <Box className={classes.userId}>
                              {u.id.slice(0, 10)}…
                            </Box>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{u.email || '—'}</TableCell>
                      <TableCell>
                        <StatusChip status={u.role || 'default'} label={u.role || '—'} />
                      </TableCell>
                      <TableCell>
                        <StatusChip status={u.status || 'inactive'} />
                      </TableCell>
                      <TableCell>{formatDate(u.createdAt)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Voir">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/admin/users/${u.id}`)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Modifier">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/admin/users/${u.id}/edit`)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton
                            size="small"
                            style={{ color: '#ef4444' }}
                            onClick={() => handleDeleteClick(u)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredUsers.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Par page"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} sur ${count}`
              }
            />
          </>
        )}
      </Paper>

      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir supprimer l'utilisateur{' '}
            <strong>
              {userToDelete?.displayName || userToDelete?.name || userToDelete?.email}
            </strong>{' '}
            ? Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Annuler</Button>
          <Button
            variant="contained"
            onClick={handleDeleteConfirm}
            style={{ background: '#ef4444', color: '#fff' }}
            autoFocus
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UsersListPage;
